import { useContentResource } from "@ctv/core";
import {
  MfaGatewayCheckResponse,
  useMfaGatewayCheck,
  useMfaGatewayRequestOtp,
  useMfaGatewayVerifyOtp,
} from "@ctv/core/auth/apis";
import { RefOtpComponent } from "@ctv/core/auth/components/mfa-otp/OtpField";
import { isMocked, mockedOtpData } from "@ctv/core/auth/constants";
import { logClientError } from "@ctv/core/utils/datadog/client";
import { useEffect, useRef, useState } from "react";

type CheckMFAGatewayData = {
  data: MfaGatewayCheckResponse | undefined | null;
  isError: boolean;
  isByPass: boolean;
  errorMessage?: string;
};

const defaultData: CheckMFAGatewayData = {
  data: undefined,
  isError: false,
  isByPass: false,
  errorMessage: undefined,
};

export function useCheckMFAGateway() {
  const [data, setData] = useState<CheckMFAGatewayData>(defaultData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const refOtp = useRef<RefOtpComponent>();

  const { CorporateEmailOtp: cr } = useContentResource();

  const check = useMfaGatewayCheck();
  const requestOtp = useMfaGatewayRequestOtp();
  const verifyOtp = useMfaGatewayVerifyOtp();

  function fetchCheckSession() {
    if (isMocked) {
      setData({ ...defaultData, data: mockedOtpData });
      return;
    }

    check()
      .then((res) => {
        if (res) {
          setData({ ...defaultData, data: res });
        } else {
          setData({ ...defaultData, data: null });
          logClientError({
            error: new Error("Failed to check MFA gateway: empty response"),
            context: {},
          });
        }
      })
      .catch((err) => {
        setData({ ...defaultData, data: null });
        logClientError({
          error: new Error("Failed to check MFA gateway API", { cause: err }),
          context: {},
        });
      });
  }

  async function onSendOtp(): Promise<boolean> {
    if (isMocked || !data.data?.email || !data.data.mfaGate) return false;

    return await requestOtp({ email: data.data.email })
      .then((res) => {
        if (!res || res.gatewayError) {
          setData((prev) => ({
            ...prev,
            isError: true,
            errorMessage: cr.resendOtpErrorText,
          }));

          logClientError({
            error: new Error("OTP request failed", {
              cause: res?.gatewayError ?? "Empty response",
            }),
            context: {},
          });

          return false;
        }

        if (
          res.gatewayMessage ===
          "Email is already verified for the current session."
        ) {
          setData((prev) => ({
            ...prev,
            isByPass: true,
          }));
        }

        return true;
      })
      .catch((err) => {
        setData((prev) => ({
          ...prev,
          isError: true,
          errorMessage: cr.resendOtpErrorText,
        }));
        logClientError({
          error: new Error("OTP request exception", { cause: err }),
          context: {},
        });
        return false;
      });
  }

  async function onSubmitOtp(otp: string) {
    if (isMocked || !data.data?.mfaGate) return;

    setIsSubmitting(true);

    await verifyOtp({ verificationToken: otp })
      .then((res) => {
        if (res && !res.gatewayError) {
          fetchCheckSession();
        }
        setData((prev) => ({
          ...prev,
          isError: !!res?.gatewayError,
          errorMessage: res?.gatewayError ? cr.submitOtpErrorText : undefined,
        }));
      })
      .catch((err) => {
        setData((prev) => ({
          ...prev,
          isError: true,
          errorMessage: cr.submitOtpErrorText,
        }));
        logClientError({
          error: new Error("OTP verification failed", { cause: err }),
          context: {},
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  useEffect(() => {
    fetchCheckSession();
  }, []);

  useEffect(() => {
    if (!data.data?.mfaGate) return;

    onSendOtp().then((success) => {
      if (!success) {
        refOtp.current?.startCountdown(0);
      }
    });
  }, [data.data?.mfaGate]);

  return {
    data,
    refOtp,
    isSubmitting,
    onSendOtp,
    onSubmitOtp,
  };
}
